import type { TaskLink } from "../types";

/** The most links one task may carry. The server enforces the same number. */
export const MAX_LINKS_PER_TASK = 10;
export const MAX_LINK_URL_LENGTH = 2083;
export const MAX_LINK_TITLE_LENGTH = 255;

/**
 * The only schemes a link may use: web links, plus the Teams and Outlook desktop deep links.
 * The server holds the same list (`LinkUrlPolicy`); a link is checked here before it is saved and
 * again before it is rendered as an `href`, so a script or file URL is never followed.
 */
export const ALLOWED_LINK_SCHEMES = ["http", "https", "msteams", "ms-outlook"] as const;

// Whitespace inside a URL is stripped by the URL parser, so `java\tscript:` would parse as a script.
// eslint-disable-next-line no-control-regex -- control characters are exactly what this looks for
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/;

/** True when `url` is an absolute URL, within the length limit, with an allowed scheme. */
export function isAllowedLinkUrl(url: string | null | undefined): url is string {
  if (typeof url !== "string" || url.length === 0 || url.length > MAX_LINK_URL_LENGTH) return false;
  if (CONTROL_CHARACTERS.test(url) || url !== url.trim()) return false;

  const scheme = /^([a-z][a-z0-9+.-]*):\/\//i.exec(url)?.[1]?.toLowerCase();
  if (!scheme || !(ALLOWED_LINK_SCHEMES as readonly string[]).includes(scheme)) return false;

  try {
    // Parsing is what rejects an empty host, such as `https://`.
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * What a person typed or pasted, as a URL to check: trimmed, and given `https://` when it has no
 * scheme but looks like a web address (`example.com/page`). A bare word such as `nope` is left as
 * it is, to be refused, rather than turned into `https://nope`. Anything with a scheme is left for
 * the allowlist to judge.
 */
export function prepareLinkUrl(input: string): string {
  const url = input.trim();
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(url);
  const looksLikeAddress = /^[^\s/?#]+\.[^\s/?#]+(?:[/?#]|$)/.test(url);
  if (url && !hasScheme && looksLikeAddress) return `https://${url}`;
  return url;
}

/**
 * A key for telling whether two links point at the same place: case, a trailing slash and a
 * `#fragment` don't change what a link opens. The query string does, so it is kept.
 */
export function linkUrlKey(url: string): string {
  return url.trim().toLowerCase().replace(/#.*$/, "").replace(/\/+$/, "");
}

/** True when the link opens in a desktop app rather than the browser. */
export function isDeepLink(url: string): boolean {
  return !/^https?:/i.test(url);
}

/** The apps a link can belong to, so it can wear the app's icon rather than a generic link one. */
export type LinkApp =
  | "teams"
  | "outlook"
  | "loop"
  | "sharepoint"
  | "onenote"
  | "azuredevops"
  | "github"
  | "gitlab"
  | "bitbucket"
  | "jira"
  | "confluence"
  | "slack"
  | "trello"
  | "asana"
  | "notion"
  | "monday"
  | "clickup"
  | "planner"
  | "googledrive"
  | "zendesk"
  | "word"
  | "excel"
  | "powerpoint"
  | "onedrive"
  | "whiteboard"
  | "figma"
  | "zoom"
  | "googledocs"
  | "googlesheets"
  | "googleslides";

const LINK_APP_SCHEMES: Record<string, LinkApp> = {
  msteams: "teams",
  "ms-outlook": "outlook",
};

// Exact hosts only: a lookalike such as `teams.microsoft.com.example.net` is not Teams.
const LINK_APP_HOSTS: Record<string, LinkApp> = {
  "teams.microsoft.com": "teams",
  "teams.live.com": "teams",
  "teams.cloud.microsoft": "teams",
  "outlook.office.com": "outlook",
  "outlook.office365.com": "outlook",
  "outlook.live.com": "outlook",
  "outlook.cloud.microsoft": "outlook",
  "loop.cloud.microsoft": "loop",
  "loop.microsoft.com": "loop",
  "onenote.com": "onenote",
  "www.onenote.com": "onenote",
  "dev.azure.com": "azuredevops",
  "github.com": "github",
  "www.github.com": "github",
  "gitlab.com": "gitlab",
  "bitbucket.org": "bitbucket",
  "trello.com": "trello",
  "app.asana.com": "asana",
  "notion.so": "notion",
  "www.notion.so": "notion",
  "app.clickup.com": "clickup",
  "tasks.office.com": "planner",
  "planner.cloud.microsoft": "planner",
  "drive.google.com": "googledrive",
  "onedrive.live.com": "onedrive",
  "1drv.ms": "onedrive",
  "whiteboard.office.com": "whiteboard",
  "whiteboard.cloud.microsoft": "whiteboard",
  "figma.com": "figma",
  "www.figma.com": "figma",
  "zoom.us": "zoom",
};

// Google Workspace documents share `docs.google.com`; the first path segment says which.
const GOOGLE_DOCS_PATHS: Record<string, LinkApp> = {
  document: "googledocs",
  spreadsheets: "googlesheets",
  presentation: "googleslides",
};

// SharePoint and OneDrive share links carry the Office app in their first path segment.
const OFFICE_SHARE_PATHS: Record<string, LinkApp> = {
  ":w:": "word",
  ":x:": "excel",
  ":p:": "powerpoint",
};

// Tenant-specific hosts (`contoso.sharepoint.com`). Matched on a whole-label suffix, so
// `sharepoint.com.example.net` and `notsharepoint.com` do not match.
const LINK_APP_HOST_SUFFIXES: ReadonlyArray<readonly [string, LinkApp]> = [
  [".sharepoint.com", "sharepoint"],
  [".visualstudio.com", "azuredevops"],
  [".slack.com", "slack"],
  [".monday.com", "monday"],
  [".zendesk.com", "zendesk"],
  [".zoom.us", "zoom"],
];

/**
 * The app a link belongs to, whether it is the app's deep link (`msteams://`, `ms-outlook://`)
 * or its web address. `undefined` for any other link.
 */
export function getLinkApp(url: string | null | undefined): LinkApp | undefined {
  if (typeof url !== "string") return undefined;
  try {
    const { protocol, hostname, pathname } = new URL(url.trim());
    const host = hostname.toLowerCase();
    // Jira and Confluence share a tenant host; Confluence lives under /wiki.
    if (host.endsWith(".atlassian.net")) {
      return pathname.toLowerCase().startsWith("/wiki") ? "confluence" : "jira";
    }
    if (host === "docs.google.com") {
      return GOOGLE_DOCS_PATHS[pathname.split("/")[1]?.toLowerCase() ?? ""];
    }
    if (host.endsWith(".sharepoint.com") || host === "onedrive.live.com") {
      const office = OFFICE_SHARE_PATHS[pathname.split("/").find((seg) => seg.startsWith(":"))?.toLowerCase() ?? ""];
      if (office) return office;
    }
    return (
      LINK_APP_SCHEMES[protocol.replace(/:$/, "").toLowerCase()] ??
      LINK_APP_HOSTS[host] ??
      LINK_APP_HOST_SUFFIXES.find(([suffix]) => host.endsWith(suffix))?.[1]
    );
  } catch {
    return undefined;
  }
}

/** Each app's artwork, in the region's source icons folder. */
const LINK_APP_ICON_FILES: Record<LinkApp, string> = {
  teams: "microsoftteams.png",
  outlook: "outlookmail.png",
  loop: "microsoftloop.svg",
  sharepoint: "sharepoint.png",
  onenote: "onenote.png",
  azuredevops: "vsts.png",
  github: "github.png",
  gitlab: "gitlab.png",
  bitbucket: "bitbucket.png",
  jira: "jira.png",
  confluence: "confluence.png",
  slack: "slack.png",
  trello: "trello.png",
  asana: "asana.png",
  notion: "notion.png",
  monday: "monday.png",
  clickup: "clickup.png",
  planner: "microsoftplanner.png",
  googledrive: "googledrive.png",
  zendesk: "zendesk.svg",
  word: "microsoftword.svg",
  excel: "excel-online.png",
  powerpoint: "microsoftpowerpoint.svg",
  onedrive: "microsoftonedrive.svg",
  whiteboard: "microsoftwhiteboard.svg",
  figma: "figma.svg",
  zoom: "zoom.svg",
  googledocs: "googledocs.svg",
  googlesheets: "google-sheets.png",
  googleslides: "googleslides.svg",
};

/**
 * Where a link's app icon is served. Leave `regionBaseUrl` out on the web, where the
 * artwork is on the same origin; the mobile app passes the region it talks to.
 */
export function getLinkAppIconUrl(app: LinkApp, regionBaseUrl = ""): string {
  return `${regionBaseUrl}/assets/img/sourceicons/${LINK_APP_ICON_FILES[app]}`;
}

/** The site a link points at, without a leading `www.`; the whole URL when it has no host. */
export function getLinkHost(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, "");
    return host || url;
  } catch {
    return url;
  }
}

/** What to call a link: its title, else its site, else the URL itself. */
export function getLinkLabel(link: Pick<TaskLink, "url" | "title">): string {
  return link.title?.trim() || getLinkHost(link.url);
}

/** The links in the order to show them, dropping any whose URL isn't allowed. Never mutates `links`. */
export function getDisplayLinks(links: readonly TaskLink[] | null | undefined): TaskLink[] {
  return [...(links ?? [])]
    .filter((link) => isAllowedLinkUrl(link.url))
    .sort((a, b) => a.order - b.order);
}
